import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import ZoomOutOutlinedIcon from '@mui/icons-material/ZoomOutOutlined';
import { Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ActionMode } from '../../hooks/useUPCActions';
import {
  ensureImageCached,
  getCachedBlobUrl,
  getCachedImageStatus,
  getCachedImageUrl,
  markImageFailed,
  markImageLoaded,
  subscribeToImageCache,
  ZoomMeta,
} from '../../hooks/useZoomPreview';
import {
  BarcodeStatus,
  ForgeDesignGroupSummary,
  MasterBarcodeEntry,
  MasterCropData,
  MasterDesignData,
  MasterUPCData,
  SectionTarget,
} from '../../types';
import {
  getMasterBarcodeGroupTheme,
  MasterBarcodeGroupTheme,
} from '../../utils/masterBarcodeGroupTheme';
import { buildDerivedBarcodeRows, getDesignBarcodeInfo } from '../../utils/masterBarcodeRows';
import {
  formatBarcodeStoreLabel,
  getBarcodeImageStripViewportWidthForAvailableSpace,
  resolveBarcodeRowResponsiveLayout,
  shortenBarcodeFromLeft,
} from '../../utils/masterLabelBarcodeRowLayout';
import { resolveMasterLabelChromeSizing } from '../../utils/masterLabelChromeSizing';
import {
  resolveMasterLabelAuxImageSizing,
  resolveMasterLabelImageSizes,
} from '../../utils/masterLabelImageSizing';
import {
  applyMasterLabelPreviewIdentity,
  buildMasterLabelBaseCropLookup,
} from '../../utils/masterLabelPreviewIdentity';
import { CropThumbnail } from './CropThumbnail';
import { ProductImagePreview } from './ProductImagePreview';

// ── Types ──

export interface FocusedHighlightInfo {
  type: string;
  cropId?: string;
  cropIds?: string[];
  cropItemNumber?: string;
}

export interface UPCColumnViewProps {
  upc: MasterUPCData;
  baseUpc?: MasterUPCData;
  // Selection
  selectedCropIds: Set<string>;
  onCropClick: (cropId: string, shiftKey: boolean) => void;
  onSectionClick: (target: SectionTarget) => void;
  // Mode
  mode: ActionMode;
  mergeSourceDesignId: string | null;
  activeDesignId: string | null;
  onActiveDesignChange: (id: string | null) => void;
  // Design colors
  designColorMap: Map<string, string>;
  // Highlight (optional — only used by Design Review)
  focusedHighlight?: FocusedHighlightInfo | null;
  isSectionHighlighted?: (sectionKey: string, target: SectionTarget, count?: number) => boolean;
  // Zoom
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
  onMetadataHover?: (e: React.MouseEvent, meta?: ZoomMeta) => void;
  onMetadataMove?: (e: React.MouseEvent, meta?: ZoomMeta) => void;
  onMetadataLeave?: () => void;
  // Optional: remove button for Master Label
  onRemoveUpc?: (upc: string) => void;
  // Show crop type triangles (demo/task player only)
  showTriangles?: boolean;
  // Recently moved crops (for glow animation)
  recentlyMovedCropIds?: Set<string>;
  selectionVisualMode?: 'default' | 'master-label';
  cropOrderMode?: 'default' | 'master-label';
  isFocusedColumn?: boolean;
  canFocusColumn?: boolean;
  onFocusColumn?: (itemNumber: string) => void;
  onClearFocusedColumn?: () => void;
  onNotify?: (text: string, type?: 'success' | 'warning' | 'error') => void;
  onOpenDesignGroups?: (design: MasterDesignData, upc: MasterUPCData) => void;
  onEditDesignNote?: (design: MasterDesignData, upc: MasterUPCData) => void;
  onLoadDesignGroupMember?: (member: ForgeDesignGroupSummary) => void;
  onOpenDataCollection?: (upc: MasterUPCData) => void;
  controlEmbeddingsUrl?: string | null;
  // Custom crop thumbnail size (also drives column width)
  cropSize?: number;
  // Text scale percentage for this column UI
  textScale?: number;
  // Retry token for crop image reloads
  reloadVersion?: number;
  // Preload a group of image URLs on first hover
  onPreloadGroup?: (urls: string[]) => void;
  highlightedCropIds?: Set<string>;
  highlightedNormalizedBarcode?: string;
  highlightedDesignId?: string;
  highlightScrollKey?: string | null;
  highlightColor?: string;
  highlightGlow?: string;
}

type CropBadgeConfig = {
  topLeftMode: 'number' | 'warning' | null;
  topLeftLabel?: string;
  topLeftBackground?: string;
  topLeftBorder?: string;
  topLeftColor?: string;
  sourceLabel: 'S' | 'F' | 'C' | 'A' | 'O';
};

type OverflowTitleTextProps = {
  as?: 'span' | 'div';
  titleText?: string;
  forceTitle?: boolean;
  style?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
  testId?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseMove?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  ariaLabel?: string;
};

type CachedNonCropImageProps = {
  imageUrl: string;
  alt: string;
  debugKey?: string;
  meta?: ZoomMeta;
  className?: string;
  testId?: string;
  containerStyle?: React.CSSProperties;
  imageStyle?: React.CSSProperties;
  fallbackStyle?: React.CSSProperties;
  fallbackIconSize?: number;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
};

const DEBUG_IMAGE_STORAGE_KEY = 'forge-debug-images';
const nonCropImageMountCounts = new Map<string, number>();

function isForgeDebugImagesEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(DEBUG_IMAGE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function debugForgeImage(event: string, details: Record<string, unknown>) {
  if (!isForgeDebugImagesEnabled()) {
    return;
  }

  console.debug('[forge-images]', event, details);
}

function countMasterLabelCrops(upc: MasterUPCData): number {
  const designCropCount = upc.designs.reduce((count, design) => count + design.crops.length, 0);
  return (
    designCropCount +
    (upc.pendingCrops?.length ?? 0) +
    upc.unassignedCrops.length +
    upc.cartonCrops.length +
    (upc.inactiveCrops?.length ?? 0)
  );
}

const OverflowTitleText: React.FC<OverflowTitleTextProps> = ({
  as = 'span',
  style,
  className,
  children,
  testId,
  onClick,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  ariaLabel,
}) => {
  return React.createElement(
    as,
    {
      style,
      className,
      'data-testid': testId,
      onClick,
      onMouseEnter,
      onMouseMove,
      onMouseLeave,
      'aria-label': ariaLabel,
    },
    children
  );
};

const CachedNonCropImage: React.FC<CachedNonCropImageProps> = ({
  imageUrl,
  alt,
  debugKey,
  meta,
  className,
  testId,
  containerStyle,
  imageStyle,
  fallbackStyle,
  fallbackIconSize = 18,
  onImageHover,
  onImageMove,
  onImageLeave,
}) => {
  const [imgError, setImgError] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState(() => getCachedImageUrl(imageUrl));
  const resolvedDebugKey = debugKey || `${alt}:${imageUrl}`;
  const hasLoggedFailureRef = React.useRef(false);

  const handleImgError = React.useCallback(() => {
    if (getCachedImageStatus(imageUrl) === 'pending') {
      return;
    }
    markImageFailed(imageUrl);
    setImgError(true);
    if (!hasLoggedFailureRef.current) {
      hasLoggedFailureRef.current = true;
      debugForgeImage('non-crop-image-failed', {
        imageUrl,
        alt,
        key: resolvedDebugKey,
      });
    }
  }, [alt, imageUrl, resolvedDebugKey]);

  const handleImgLoad = React.useCallback(() => {
    markImageLoaded(imageUrl);
    setImgError(false);
    hasLoggedFailureRef.current = false;
  }, [imageUrl]);

  React.useEffect(() => {
    const previousMountCount = nonCropImageMountCounts.get(resolvedDebugKey) ?? 0;
    nonCropImageMountCounts.set(resolvedDebugKey, previousMountCount + 1);
    debugForgeImage(previousMountCount > 0 ? 'non-crop-image-remount' : 'non-crop-image-mount', {
      imageUrl,
      alt,
      key: resolvedDebugKey,
      mounts: previousMountCount + 1,
    });

    return () => {
      const currentMountCount = nonCropImageMountCounts.get(resolvedDebugKey) ?? 0;
      if (currentMountCount <= 1) {
        nonCropImageMountCounts.delete(resolvedDebugKey);
        return;
      }
      nonCropImageMountCounts.set(resolvedDebugKey, currentMountCount - 1);
    };
  }, [alt, imageUrl, resolvedDebugKey]);

  React.useEffect(() => {
    const cachedBlobUrl = getCachedBlobUrl(imageUrl);
    setImageSrc(cachedBlobUrl ?? imageUrl);
    setImgError(getCachedImageStatus(imageUrl) === 'failed' && !cachedBlobUrl);
    ensureImageCached(imageUrl);

    const unsubscribe = subscribeToImageCache(imageUrl, () => {
      const blobUrl = getCachedBlobUrl(imageUrl);
      if (blobUrl) {
        setImageSrc(blobUrl);
        setImgError(false);
        return;
      }

      if (getCachedImageStatus(imageUrl) === 'failed') {
        setImgError(true);
        if (!hasLoggedFailureRef.current) {
          hasLoggedFailureRef.current = true;
          debugForgeImage('non-crop-image-failed', {
            imageUrl,
            alt,
            key: resolvedDebugKey,
            source: 'cache-status',
          });
        }
      }
    });

    return unsubscribe;
  }, [alt, imageUrl, resolvedDebugKey]);

  return (
    <div
      className={className}
      data-testid={testId}
      data-image-state={imgError ? 'failed' : 'ready'}
      style={containerStyle}
      onMouseEnter={!imgError && onImageHover ? (e) => onImageHover(e, imageUrl, meta) : undefined}
      onMouseMove={!imgError && onImageMove ? (e) => onImageMove(e, imageUrl, meta) : undefined}
      onMouseLeave={!imgError ? onImageLeave : undefined}
    >
      {imgError ? (
        <div
          data-testid={testId ? `${testId}-fallback` : undefined}
          style={{
            width: '100%',
            height: '100%',
            background: '#1a1a1a',
            color: 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...fallbackStyle,
          }}
        >
          <BrokenImageOutlinedIcon
            style={{
              fontSize: fallbackIconSize,
              opacity: 0.55,
            }}
          />
        </div>
      ) : (
        <img
          src={imageSrc}
          alt={alt}
          style={imageStyle}
          draggable={false}
          onLoad={handleImgLoad}
          onError={handleImgError}
        />
      )}
    </div>
  );
};

// ── Component ──

export const UPCColumnView: React.FC<UPCColumnViewProps> = ({
  upc,
  baseUpc,
  selectedCropIds,
  onCropClick,
  onSectionClick,
  mode,
  mergeSourceDesignId,
  activeDesignId,
  onActiveDesignChange,
  designColorMap,
  focusedHighlight,
  isSectionHighlighted,
  onImageHover,
  onImageMove,
  onImageLeave,
  onMetadataHover,
  onMetadataMove,
  onMetadataLeave,
  onRemoveUpc,
  showTriangles = false,
  recentlyMovedCropIds,
  selectionVisualMode = 'default',
  cropOrderMode = 'default',
  isFocusedColumn = false,
  canFocusColumn = false,
  onFocusColumn,
  onClearFocusedColumn,
  onNotify,
  onOpenDesignGroups,
  onEditDesignNote,
  onLoadDesignGroupMember,
  onOpenDataCollection,
  controlEmbeddingsUrl,
  cropSize,
  textScale = 100,
  reloadVersion = 0,
  onPreloadGroup,
  highlightedCropIds,
  highlightedNormalizedBarcode,
  highlightedDesignId,
  highlightScrollKey,
  highlightColor,
  highlightGlow,
}) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set());
  const [barcodeSectionWidth, setBarcodeSectionWidth] = React.useState(680);
  const preloadedGroups = React.useRef(new Set<string>());
  const barcodeSectionRef = React.useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const lastAppliedHighlightScrollKeyRef = React.useRef<string | null>(null);
  const HEADER_SIDE_WIDTH = 22;
  const { cropSizePx: resolvedCropSize, referenceSizePx: resolvedReferenceImageSize } =
    resolveMasterLabelImageSizes(cropSize ?? 100);
  const {
    stackHeightPx: resolvedAuxStackHeight,
    gapPx: resolvedAuxGap,
    slotSizePx: resolvedAuxSlotSize,
  } = resolveMasterLabelAuxImageSizing(resolvedReferenceImageSize);
  const resolvedTextScale = textScale / 100;
  const scaleText = (px: number) => Math.round(px * resolvedTextScale * 100) / 100;
  const scaleIcon = (px: number, min = 10) => Math.max(min, scaleText(px));
  const chrome = React.useMemo(() => resolveMasterLabelChromeSizing(textScale), [textScale]);
  const upcEntryImageSize = resolvedReferenceImageSize;
  const upcBadgeColumnWidth = 16;
  const upcEntryColumnGap = 8;
  const BARCODE_ROW_TEXT_MIN_WIDTH = 140;
  const BARCODE_ROW_FIXED_CHROME_WIDTH =
    HEADER_SIDE_WIDTH + upcBadgeColumnWidth + upcEntryColumnGap * 3 + 28;
  const columnId = upc.itemNumber;
  const normalizedBarcodeHighlight = highlightedNormalizedBarcode?.trim() || null;
  const resolvedBarcodeHighlightColor = highlightColor || 'var(--purple)';
  const { barcodeRows, barcodeEntryByNormalized } = React.useMemo(
    () => buildDerivedBarcodeRows(upc),
    [upc]
  );
  const baseCropById = React.useMemo(
    () => buildMasterLabelBaseCropLookup(baseUpc ?? upc),
    [baseUpc, upc]
  );

  const upcCount = barcodeRows.length;
  const barcodeGroupThemeByNormalized = new Map(
    barcodeRows
      .map(
        (row, index) =>
          [
            row.normalizedBarcode,
            {
              number: index + 1,
              theme: getMasterBarcodeGroupTheme(index),
            },
          ] as const
      )
      .filter(([normalized]) => Boolean(normalized))
      .filter(
        ([normalized], index, entries) =>
          entries.findIndex(([value]) => value === normalized) === index
      )
  );
  const knownBarcodes = new Set(barcodeRows.map((row) => row.normalizedBarcode).filter(Boolean));

  useEffect(() => {
    if (!highlightScrollKey || !highlightedCropIds || highlightedCropIds.size === 0) return;
    const scopedScrollKey = `${highlightScrollKey}:${columnId}`;
    if (lastAppliedHighlightScrollKeyRef.current === scopedScrollKey) return;

    const frame = window.requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const firstHighlightedTile = Array.from(
        container.querySelectorAll<HTMLElement>('[data-crop-id]')
      ).find((element) => {
        const cropId = element.getAttribute('data-crop-id');
        return cropId ? highlightedCropIds.has(cropId) : false;
      });
      if (!firstHighlightedTile) return;

      const containerRect = container.getBoundingClientRect();
      const tileRect = firstHighlightedTile.getBoundingClientRect();
      const nextScrollTop =
        container.scrollTop +
        (tileRect.top - containerRect.top) -
        container.clientHeight / 2 +
        tileRect.height / 2;

      container.scrollTo({
        top: Math.max(0, nextScrollTop),
        behavior: 'auto',
      });
      lastAppliedHighlightScrollKeyRef.current = scopedScrollKey;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [columnId, highlightedCropIds, highlightScrollKey]);

  const preloadGroup = (groupKey: string, crops: MasterCropData[]) => {
    if (!onPreloadGroup || preloadedGroups.current.has(groupKey)) return;
    preloadedGroups.current.add(groupKey);
    onPreloadGroup(crops.map((c) => c.imageUrl));
  };

  const resolvePreviewCrop = (crop: MasterCropData): MasterCropData =>
    applyMasterLabelPreviewIdentity(crop, baseCropById.get(crop.id));

  // Stable sort — optionally front-first, then scan-sourced crops, then ID order.
  const sorted = (crops: MasterCropData[], options?: { frontFirst?: boolean }) =>
    [...crops].sort((a, b) => {
      const aPreview = resolvePreviewCrop(a);
      const bPreview = resolvePreviewCrop(b);
      if (options?.frontFirst ?? cropOrderMode === 'default') {
        const aFront = aPreview.orientation === 'not-front' ? 1 : 0;
        const bFront = bPreview.orientation === 'not-front' ? 1 : 0;
        if (aFront !== bFront) return aFront - bFront;
      }
      const aScan = aPreview.source === 'scan' ? 0 : 1;
      const bScan = bPreview.source === 'scan' ? 0 : 1;
      if (aScan !== bScan) return aScan - bScan;
      return a.id.localeCompare(b.id);
    });
  const getCropSectionType = (crop: MasterCropData): 'front' | 'non-front' | 'carton' =>
    crop.classificationType === 'carton'
      ? 'carton'
      : crop.orientation === 'not-front'
        ? 'non-front'
        : 'front';
  const cropNeedsBarcodeWarning = (crop: MasterCropData) =>
    crop.source === 'scan' &&
    crop.classificationType !== 'carton' &&
    knownBarcodes.size > 0 &&
    !knownBarcodes.has(crop.normalizedBarcode || '');
  const getCropSourceBadgeLabel = (crop: MasterCropData): 'S' | 'F' | 'C' | 'A' | 'O' =>
    crop.source === 'scan'
      ? 'S'
      : crop.source === 'forge'
        ? 'F'
        : crop.source === 'control'
          ? 'C'
          : crop.source === 'at'
            ? 'A'
            : 'O';
  const getCropBadgeConfig = (
    crop: MasterCropData,
    sectionType: 'front' | 'non-front' | 'carton'
  ): CropBadgeConfig => {
    const baseConfig = {
      sourceLabel: getCropSourceBadgeLabel(crop),
    };

    if (crop.source !== 'scan') {
      return {
        ...baseConfig,
        topLeftMode: null,
      } as const;
    }

    const matchedBadge = barcodeGroupThemeByNormalized.get(crop.normalizedBarcode || '');
    if (matchedBadge) {
      return {
        ...baseConfig,
        topLeftMode: 'number' as const,
        topLeftLabel: String(matchedBadge.number),
        topLeftBackground: matchedBadge.theme.badgeBackground,
        topLeftBorder: matchedBadge.theme.badgeBorder,
        topLeftColor: matchedBadge.theme.badgeText,
      };
    }

    return {
      ...baseConfig,
      topLeftMode: cropNeedsBarcodeWarning(crop) ? ('warning' as const) : null,
    };
  };
  const buildCropZoomMeta = (crop: MasterCropData): ZoomMeta => ({
    id: crop.id,
    storeId: crop.storeId,
    source: crop.source,
    forgeOriginSource: crop.forgeOriginSource,
    barcode: crop.barcode,
    normalizedBarcode: crop.normalizedBarcode,
    normalizedUpc: crop.normalizedUpc,
    classificationType: crop.classificationType,
    warningMessage: cropNeedsBarcodeWarning(crop)
      ? 'Barcode does not match known barcodes'
      : undefined,
    imageResolution: crop.imageResolution,
    createdBy: crop.createdBy,
    createdAt: crop.createdAt,
    updatedBy: crop.updatedBy,
    updatedAt: crop.updatedAt,
  });
  function getEntryImages(entry: MasterBarcodeEntry) {
    const seen = new Set<string>();
    return entry.storeDetails
      .flatMap((detail) => detail.images)
      .filter((image) => {
        const key = `${image.url}::${image.sourceLabel || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }
  function getEntrySharedDisplay(entry: MasterBarcodeEntry) {
    const details = entry.storeDetails;
    if (details.length === 0) return null;
    const signatures = details.map((detail) =>
      JSON.stringify({
        name: detail.name || '',
        casePack: detail.casePack ?? null,
        images: detail.images.map((image) => image.url),
      })
    );
    if (signatures.every((signature) => signature === signatures[0])) {
      return details[0];
    }
    return null;
  }
  const getResolvedDesignBarcodeInfo = (design: (typeof upc.designs)[number]) =>
    getDesignBarcodeInfo(design, barcodeEntryByNormalized);
  const toggleCollapsedSection = (sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };
  const getEntryShellPadding = (hasImages: boolean) =>
    hasImages
      ? `${chrome.barcodeRowShellPaddingTopWithImages}px 8px ${chrome.barcodeRowShellPaddingBottomWithImages}px 4px`
      : `${chrome.barcodeRowShellPaddingYWithoutImages}px 8px ${chrome.barcodeRowShellPaddingYWithoutImages}px 4px`;
  const getEntryCardPadding = (hasImages: boolean) =>
    hasImages
      ? `${chrome.barcodeRowCardPaddingYWithImages}px 6px ${chrome.barcodeRowCardPaddingYWithImages}px 2px`
      : `${chrome.barcodeRowCardPaddingYWithoutImages}px 6px ${chrome.barcodeRowCardPaddingYWithoutImages}px 2px`;
  const getEntryContentGap = (hasImages: boolean) =>
    hasImages ? chrome.barcodeRowContentGapWithImages : chrome.barcodeRowContentGapWithoutImages;
  const getEntryTextGap = (hasImages: boolean) =>
    hasImages ? chrome.barcodeRowTextGapWithImages : chrome.barcodeRowTextGapWithoutImages;
  const headerSurface = 'var(--elevated)';
  const softDivider = 'color-mix(in srgb, var(--border) 70%, transparent)';
  const notifyCopyResult = React.useCallback(
    (text: string, type: 'success' | 'warning' | 'error' = 'success') => {
      onNotify?.(text, type);
    },
    [onNotify]
  );
  const copyText = React.useCallback(
    async (value: string, successMessage: string) => {
      if (selectedCropIds.size > 0) {
        return;
      }
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          notifyCopyResult(successMessage);
          return;
        }

        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', 'true');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        document.body.appendChild(textarea);
        textarea.select();
        const didCopy = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (didCopy) {
          notifyCopyResult(successMessage);
          return;
        }
      } catch (error) {
        // Fall through to the shared error notification below.
      }

      notifyCopyResult('Copy failed', 'error');
    },
    [notifyCopyResult, selectedCropIds.size]
  );

  React.useEffect(() => {
    const node = barcodeSectionRef.current;
    if (!node) {
      return;
    }

    const updateWidth = (width?: number) => {
      const nextWidth = width ?? node.getBoundingClientRect().width;
      if (nextWidth > 0) {
        setBarcodeSectionWidth(nextWidth);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        updateWidth(entry.contentRect.width);
      }
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [barcodeRows.length]);
  const renderUpcNumberBadge = (normalizedBarcode: string) => {
    const badge = barcodeGroupThemeByNormalized.get(normalizedBarcode);
    if (!badge) return null;
    return (
      <div
        style={{
          width: 16,
          height: 16,
          padding: 0,
          borderRadius: 999,
          background: badge.theme.badgeBackground,
          border: badge.theme.badgeBorder,
          color: badge.theme.badgeText,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: scaleText(8),
          fontWeight: 700,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {badge.number}
      </div>
    );
  };
  const renderBarcodeImages = (
    images: { url: string; sourceLabel?: string }[],
    normalizedBarcode: string,
    viewportWidth: number,
    imageSize = upcEntryImageSize,
    testId?: string,
    theme?: MasterBarcodeGroupTheme
  ) => {
    return (
      <div
        data-testid={testId}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          width: viewportWidth,
          maxWidth: viewportWidth,
          overflowX: images.length > 1 ? 'auto' : 'hidden',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'thin',
        }}
      >
        {images.map((image) => (
          <div
            key={`${image.url}-${image.sourceLabel || 'image'}`}
            style={{
              width: imageSize,
              height: imageSize,
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${theme?.cardBorder || 'var(--border)'}`,
              background: '#000',
              flex: '0 0 auto',
            }}
          >
            <CachedNonCropImage
              imageUrl={image.url}
              alt={image.sourceLabel || 'barcode image'}
              debugKey={`barcode-strip:${columnId}:${normalizedBarcode}:${image.url}`}
              meta={image.sourceLabel ? { source: image.sourceLabel } : undefined}
              containerStyle={{ width: '100%', height: '100%' }}
              imageStyle={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
                background: '#000',
              }}
              fallbackStyle={{ background: '#000' }}
              onImageHover={onImageHover}
              onImageMove={onImageMove}
              onImageLeave={onImageLeave}
            />
          </div>
        ))}
      </div>
    );
  };
  // ── Render helpers ──

  const barcodeImageViewportWidthByNormalized = React.useMemo(() => {
    const maxAvailableImageWidth = Math.max(
      0,
      barcodeSectionWidth - BARCODE_ROW_FIXED_CHROME_WIDTH - BARCODE_ROW_TEXT_MIN_WIDTH
    );
    return new Map(
      barcodeRows.map((row) => [
        row.normalizedBarcode,
        getBarcodeImageStripViewportWidthForAvailableSpace(
          getEntryImages(row.entry).length,
          upcEntryImageSize,
          maxAvailableImageWidth
        ),
      ])
    );
  }, [BARCODE_ROW_FIXED_CHROME_WIDTH, barcodeRows, barcodeSectionWidth, upcEntryImageSize]);
  const sharedBarcodeTextWidth = Math.max(
    96,
    barcodeRows.length > 0
      ? Math.min(
          ...barcodeRows.map(
            (row) =>
              barcodeSectionWidth -
              BARCODE_ROW_FIXED_CHROME_WIDTH -
              (barcodeImageViewportWidthByNormalized.get(row.normalizedBarcode) ?? 0)
          )
        )
      : barcodeSectionWidth - BARCODE_ROW_FIXED_CHROME_WIDTH
  );
  const sharedBarcodeRowLayout = resolveBarcodeRowResponsiveLayout(sharedBarcodeTextWidth);

  const renderResponsiveBarcodeRowCard = (
    row: (typeof barcodeRows)[number],
    rowTheme: MasterBarcodeGroupTheme,
    rowSectionKey: string,
    isRowCollapsed: boolean
  ) => {
    const sharedDisplay = getEntrySharedDisplay(row.entry);
    const entryImages = getEntryImages(row.entry);
    const imageViewportWidth =
      barcodeImageViewportWidthByNormalized.get(row.normalizedBarcode) ?? 0;
    const hasImages = entryImages.length > 0;
    const entryNumberBadge = renderUpcNumberBadge(row.normalizedBarcode);
    const rowLayout = sharedBarcodeRowLayout;
    const barcodeLabel = shortenBarcodeFromLeft(row.entry.upc, rowLayout.barcodeMaxCharacters);
    const isBarcodeLabelHighlighted =
      normalizedBarcodeHighlight !== null && row.normalizedBarcode === normalizedBarcodeHighlight;
    const hasCasePackWarning = Boolean(row.warningMessage);

    const renderSharedEntryText = () => {
      if (!sharedDisplay) {
        return null;
      }

      const fullStoresLabel = row.entry.storeDetails
        .map((detail) => formatBarcodeStoreLabel(detail, false))
        .join('/');
      const productLabel =
        `${sharedDisplay.name || ''}${sharedDisplay.casePack ? ` (${sharedDisplay.casePack})` : ''}`.trim();

      if (rowLayout.textLayoutMode === 'split') {
        return (
          <div
            data-testid={`barcode-shared-text-${columnId}-${row.normalizedBarcode}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: getEntryTextGap(hasImages),
              minWidth: 0,
              fontFamily: 'var(--font-mono)',
              fontSize: scaleText(9),
              lineHeight: 1.35,
            }}
          >
            <OverflowTitleText
              as="div"
              testId={`barcode-store-line-${columnId}-${row.normalizedBarcode}`}
              titleText={fullStoresLabel}
              forceTitle={rowLayout.abbreviateStores}
              style={{
                color: 'var(--text-tertiary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.entry.storeDetails.map((detail, index) => (
                <React.Fragment key={`${row.entry.upc}-${detail.storeLabel}-${detail.role}`}>
                  {index > 0 ? '/' : null}
                  <span style={{ fontWeight: detail.role === 'primary' ? 700 : 400 }}>
                    {formatBarcodeStoreLabel(detail, rowLayout.abbreviateStores)}
                  </span>
                </React.Fragment>
              ))}
            </OverflowTitleText>
            <OverflowTitleText
              testId={`barcode-product-line-${columnId}-${row.normalizedBarcode}`}
              titleText={productLabel}
              style={{
                color: 'var(--text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {productLabel}
            </OverflowTitleText>
          </div>
        );
      }

      return (
        <div
          data-testid={`barcode-shared-text-${columnId}-${row.normalizedBarcode}`}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            minWidth: 0,
            fontFamily: 'var(--font-mono)',
            fontSize: scaleText(9),
            lineHeight: 1.35,
          }}
        >
          <OverflowTitleText
            as="div"
            testId={`barcode-store-line-${columnId}-${row.normalizedBarcode}`}
            titleText={fullStoresLabel}
            forceTitle={rowLayout.abbreviateStores}
            style={{
              color: 'var(--text-tertiary)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'baseline',
              gap: 0,
            }}
          >
            {row.entry.storeDetails.map((detail, index) => (
              <React.Fragment key={`${row.entry.upc}-${detail.storeLabel}-${detail.role}`}>
                {index > 0 ? '/' : null}
                <span style={{ fontWeight: detail.role === 'primary' ? 700 : 400 }}>
                  {formatBarcodeStoreLabel(detail, rowLayout.abbreviateStores)}
                </span>
              </React.Fragment>
            ))}
          </OverflowTitleText>
          <OverflowTitleText
            testId={`barcode-product-line-${columnId}-${row.normalizedBarcode}`}
            titleText={productLabel}
            style={{
              color: 'var(--text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {productLabel}
          </OverflowTitleText>
        </div>
      );
    };

    const renderDetailedEntryText = () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: getEntryTextGap(hasImages),
          minWidth: 0,
        }}
      >
        {row.entry.storeDetails.map((detail) => {
          const storeLabel = formatBarcodeStoreLabel(detail, rowLayout.abbreviateStores);
          const fullStoreLabel = formatBarcodeStoreLabel(detail, false);
          const productLabel =
            `${detail.name || ''}${detail.casePack ? ` (${detail.casePack})` : ''}`.trim();

          return (
            <div
              key={`${row.entry.upc}-${detail.storeLabel}-${detail.role}`}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                minWidth: 0,
                fontFamily: 'var(--font-mono)',
                fontSize: scaleText(9),
                lineHeight: 1.35,
              }}
            >
              <OverflowTitleText
                titleText={fullStoreLabel}
                forceTitle={rowLayout.abbreviateStores}
                style={{
                  color: 'var(--text-tertiary)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  fontWeight: detail.role === 'primary' ? 700 : 400,
                }}
              >
                {storeLabel}
              </OverflowTitleText>
              <OverflowTitleText
                titleText={productLabel}
                style={{
                  color: 'var(--text-secondary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {productLabel}
              </OverflowTitleText>
            </div>
          );
        })}
      </div>
    );

    return (
      <div
        key={row.key}
        data-testid={`barcode-row-shell-${columnId}-${row.normalizedBarcode}`}
        data-barcode-theme={rowTheme.key}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: hasImages
            ? chrome.barcodeRowTextGapWithImages
            : chrome.barcodeRowTextGapWithoutImages,
          padding: getEntryShellPadding(hasImages),
          borderRadius: 'calc(var(--radius-sm) + 1px)',
          background: hasCasePackWarning ? 'rgba(245, 158, 11, 0.08)' : rowTheme.sectionBackground,
          boxShadow: `inset 0 0 0 1px ${
            hasCasePackWarning ? 'rgba(245, 158, 11, 0.42)' : rowTheme.cardBorder
          }, var(--shadow-card)`,
        }}
      >
        <div
          data-testid={`barcode-row-card-${columnId}-${row.normalizedBarcode}`}
          data-barcode-theme={rowTheme.key}
          style={{
            display: 'grid',
            gridTemplateColumns: hasImages
              ? `${HEADER_SIDE_WIDTH}px ${upcBadgeColumnWidth}px minmax(0, auto) minmax(0, 1fr) auto`
              : `${HEADER_SIDE_WIDTH}px ${upcBadgeColumnWidth}px minmax(0, auto) minmax(0, 1fr)`,
            columnGap: upcEntryColumnGap,
            alignItems: 'center',
            padding: getEntryCardPadding(hasImages),
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${rowTheme.cardBorder}`,
            borderLeft: hasCasePackWarning
              ? '3px solid rgba(245, 158, 11, 0.78)'
              : '3px solid transparent',
            background: rowTheme.cardBackground,
            cursor: 'default',
            transition: 'all 150ms ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              data-testid={`barcode-row-chevron-${columnId}-${row.normalizedBarcode}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapsedSection(rowSectionKey);
              }}
              style={{ cursor: 'pointer', display: 'flex' }}
            >
              {isRowCollapsed ? (
                <KeyboardArrowRightIcon
                  style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
                />
              )}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {entryNumberBadge}
          </div>
          <div
            data-testid={`barcode-label-${columnId}-${row.normalizedBarcode}`}
            aria-label={`Copy UPC ${row.entry.upc}`}
            onClick={() => {
              void copyText(row.entry.upc, 'Copied UPC');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: scaleText(9),
              color: isBarcodeLabelHighlighted ? resolvedBarcodeHighlightColor : rowTheme.upcText,
              fontWeight: isBarcodeLabelHighlighted ? 700 : 400,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              borderRadius: 4,
              padding: `${chrome.headerActionInset}px 2px`,
            }}
          >
            {barcodeLabel}
          </div>
          <div
            style={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: getEntryContentGap(hasImages),
            }}
          >
            {row.isCasePack ? (
              row.warningMessage ? (
                <Tooltip title={row.warningMessage} arrow>
                  <div
                    data-testid={`barcode-row-warning-${columnId}-${row.normalizedBarcode}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      width: 'fit-content',
                      maxWidth: '100%',
                      color: '#f59e0b',
                      fontFamily: 'var(--font-mono)',
                      fontSize: scaleText(8),
                      fontWeight: 700,
                      letterSpacing: 0,
                      textTransform: 'uppercase',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <WarningAmberOutlinedIcon style={{ fontSize: scaleIcon(12) }} />
                    Case pack
                  </div>
                </Tooltip>
              ) : (
                <div
                  data-testid={`barcode-row-case-pack-label-${columnId}-${row.normalizedBarcode}`}
                  style={{
                    display: 'inline-flex',
                    width: 'fit-content',
                    maxWidth: '100%',
                    color: 'var(--text-tertiary)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: scaleText(8),
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: 'uppercase',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Case pack
                </div>
              )
            ) : null}
            {sharedDisplay ? renderSharedEntryText() : renderDetailedEntryText()}
          </div>
          {entryImages.length > 0 ? (
            <div style={{ justifySelf: 'end', minWidth: 0 }}>
              {renderBarcodeImages(
                entryImages,
                row.normalizedBarcode,
                imageViewportWidth,
                upcEntryImageSize,
                `barcode-images-${columnId}-${row.normalizedBarcode}`,
                rowTheme
              )}
            </div>
          ) : null}
        </div>

        {!isRowCollapsed && row.matchedDesigns.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: chrome.sectionStackGap,
              marginLeft: chrome.sectionHeaderPaddingY,
              paddingLeft: chrome.sectionHeaderPaddingY,
              borderLeft: `2px solid ${rowTheme.nestedRail}`,
            }}
          >
            {row.matchedDesigns.map((design) =>
              renderDesignSection(design, `barcode-${row.normalizedBarcode}`, rowTheme)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCrop = (
    crop: MasterCropData,
    sectionType: 'front' | 'non-front' | 'carton',
    designId?: string,
    isRepresentative = false,
    isPending = false
  ) => {
    const previewCrop = resolvePreviewCrop(crop);
    const isFocusedCrop = !!(
      focusedHighlight &&
      (focusedHighlight.cropId === crop.id ||
        (focusedHighlight.cropIds && focusedHighlight.cropIds.includes(crop.id)))
    );
    const isLinkHighlighted = highlightedCropIds?.has(crop.id) ?? false;
    const badgeConfig = getCropBadgeConfig(previewCrop, sectionType);
    return (
      <CropThumbnail
        key={crop.id}
        crop={previewCrop}
        sectionType={sectionType}
        size={resolvedCropSize}
        selectionVisualMode={selectionVisualMode}
        isSelected={selectedCropIds.has(crop.id)}
        isRepresentative={isRepresentative}
        isFocused={isFocusedCrop}
        isLinkHighlighted={isLinkHighlighted}
        highlightColor={highlightColor}
        highlightGlow={highlightGlow}
        isPending={isPending}
        showDeletionMark={Boolean(previewCrop.markedForDeletion)}
        showTriangle={showTriangles}
        isRecentlyMoved={recentlyMovedCropIds?.has(crop.id) ?? false}
        reloadVersion={reloadVersion}
        topLeftBadgeMode={badgeConfig.topLeftMode}
        topLeftBadgeLabel={badgeConfig.topLeftLabel}
        topLeftBadgeBackground={badgeConfig.topLeftBackground}
        topLeftBadgeBorder={badgeConfig.topLeftBorder}
        topLeftBadgeColor={badgeConfig.topLeftColor}
        sourceBadgeLabel={badgeConfig.sourceLabel}
        onClick={(e) => {
          e.stopPropagation();
          onCropClick(crop.id, e.shiftKey);
        }}
        onImageHover={onImageHover}
        onImageMove={onImageMove}
        onImageLeave={onImageLeave}
      />
    );
  };

  const renderCropGrid = (
    crops: MasterCropData[],
    testId: string,
    sectionTypeResolver: (crop: MasterCropData) => 'front' | 'non-front' | 'carton',
    options?: {
      groupKey: string;
      pending?: boolean;
      representativeCropId?: string | null;
      designId?: string;
      frontFirst?: boolean;
    }
  ) => {
    const sortedCrops = sorted(crops, { frontFirst: options?.frontFirst });

    return (
      <div
        data-testid={testId}
        onMouseEnter={() => {
          if (options?.groupKey) {
            preloadGroup(options.groupKey, sortedCrops);
          }
        }}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          width: '100%',
          padding: '2px 8px 4px 0',
          alignContent: 'flex-start',
        }}
      >
        {sortedCrops.map((crop) =>
          renderCrop(
            crop,
            sectionTypeResolver(crop),
            options?.designId,
            crop.id === options?.representativeCropId,
            options?.pending
          )
        )}
      </div>
    );
  };

  const renderSectionHeader = (
    label: string,
    count: number,
    sectionKey: string,
    target: SectionTarget,
    options?: { color?: string; isMergeSource?: boolean; barcodeStatus?: BarcodeStatus }
  ) => {
    const isCollapsed = collapsedSections.has(sectionKey);
    const isExplicitDesignTarget = target.type !== 'design' || !!target.designId;
    const isDropTarget =
      (mode === 'crops-selected' ||
        (mode === 'merge-source' &&
          target.type === 'design' &&
          target.designId !== mergeSourceDesignId)) &&
      target.type !== 'pending' &&
      isExplicitDesignTarget;
    const isClickable = isDropTarget || options?.isMergeSource || mode === 'idle';
    const isMergeSource = options?.isMergeSource;

    // Layout: [collapse 22px fixed] [label ... flex] [count]
    // The 22px column ensures labels always start at the same X regardless of collapse icon
    return (
      <div
        onClick={() => {
          if (isDropTarget || isMergeSource || mode === 'idle') {
            onSectionClick(target);
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: `${chrome.sectionHeaderPaddingY}px 10px ${chrome.sectionHeaderPaddingY}px 2px`,
          cursor: isClickable ? 'pointer' : 'default',
          borderRadius: 'var(--radius-sm)',
          borderLeft: isMergeSource ? '3px solid var(--danger)' : '3px solid transparent',
          border: `1px solid ${isMergeSource ? 'var(--danger)' : softDivider}`,
          background: isMergeSource ? 'var(--danger-subtle)' : headerSurface,
          boxShadow: 'var(--shadow-card)',
          transition: 'all 150ms ease',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => {
          if (isDropTarget) {
            e.currentTarget.style.background =
              mode === 'merge-source' ? 'var(--positive-subtle)' : 'var(--purple-subtle)';
            e.currentTarget.style.borderLeftColor =
              mode === 'merge-source' ? 'var(--positive)' : 'var(--purple)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isMergeSource ? 'var(--danger-subtle)' : 'transparent';
          e.currentTarget.style.borderLeftColor = isMergeSource ? 'var(--danger)' : 'transparent';
        }}
      >
        <div
          style={{
            width: HEADER_SIDE_WIDTH,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {count > 0 ? (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setCollapsedSections((prev) => {
                  const n = new Set(prev);
                  if (n.has(sectionKey)) n.delete(sectionKey);
                  else n.add(sectionKey);
                  return n;
                });
              }}
              style={{ cursor: 'pointer', display: 'flex' }}
            >
              {isCollapsed ? (
                <KeyboardArrowRightIcon
                  style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
                />
              )}
            </div>
          ) : (
            <KeyboardArrowRightIcon style={{ fontSize: scaleIcon(16), color: 'transparent' }} />
          )}
        </div>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: scaleText(9),
            fontWeight: 700,
            color: isMergeSource ? 'var(--danger)' : 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            flex: 1,
            textAlign: 'center',
          }}
        >
          {label}
        </span>
        {isMergeSource && (
          <span
            style={{
              fontSize: scaleText(8),
              fontWeight: 700,
              color: 'var(--danger)',
              textTransform: 'uppercase',
              fontFamily: 'var(--font-mono)',
            }}
          >
            SOURCE
          </span>
        )}
      </div>
    );
  };

  const checkHighlight = (sectionKey: string, target: SectionTarget, count?: number) => {
    return isSectionHighlighted ? isSectionHighlighted(sectionKey, target, count) : false;
  };

  const renderStaticHeader = (label: string, count: number, sectionKey: string) => {
    const isCollapsed = collapsedSections.has(sectionKey);

    return (
      <div
        onClick={() => toggleCollapsedSection(sectionKey)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: `${chrome.sectionHeaderPaddingY}px 10px ${chrome.sectionHeaderPaddingY}px 2px`,
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${softDivider}`,
          background: headerSurface,
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div
          style={{
            width: HEADER_SIDE_WIDTH,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isCollapsed ? (
            <KeyboardArrowRightIcon
              style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
            />
          ) : (
            <KeyboardArrowDownIcon
              style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
            />
          )}
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'var(--font-mono)',
            fontSize: scaleText(9),
            fontWeight: 700,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: 'var(--text-tertiary)',
            textAlign: 'center',
          }}
        >
          {label}
        </div>
      </div>
    );
  };

  const renderDesignSection = (
    design: (typeof upc.designs)[number],
    groupKeyPrefix: string,
    barcodeTheme: MasterBarcodeGroupTheme
  ) => {
    const sectionKey = `design-${design.id}`;
    const isMergeSource = mergeSourceDesignId === design.id;
    const designTarget: SectionTarget = {
      itemNumber: columnId,
      type: 'design',
      designId: design.id,
    };
    const isDesignHighlighted = checkHighlight(sectionKey, designTarget);

    const isConfirmed = design.barcodeStatus === 'confirmed';
    const isBarcPending = design.barcodeStatus === 'pending';
    const designBarcodeInfo = getResolvedDesignBarcodeInfo(design);
    const showReliableDesignLabel = Boolean(designBarcodeInfo.normalizedBarcode);
    const designTitleText = showReliableDesignLabel
      ? designBarcodeInfo.productLabel || designBarcodeInfo.normalizedBarcodeValue || 'Unknown'
      : 'Unknown';
    const designBarcodeText = showReliableDesignLabel
      ? designBarcodeInfo.normalizedBarcodeValue || ''
      : '';
    const noteCount = design.notes?.length ?? 0;
    const designZoomMeta: ZoomMeta = {
      metadataType: 'design',
      id: design.id,
      normalizedBarcode: design.normalizedBarcode ?? undefined,
      barcodeStatus: design.barcodeStatus ?? undefined,
      createdAt: design.createdAt ?? undefined,
      updatedAt: design.updatedAt ?? undefined,
      updatedBy: design.updatedBy ?? undefined,
      cropCount: design.crops.length,
      noteCount,
    };
    const designCrops = sorted(design.crops, { frontFirst: true });
    const repCrop = designCrops.find((crop) => crop.id === design.representativeCropId);
    const showCropStrip = designCrops.length > 0;
    const refImgSize = resolvedReferenceImageSize;
    const badgeSize = Math.max(11, Math.round(resolvedReferenceImageSize * 0.32));
    const iconSize = Math.max(14, Math.round(resolvedReferenceImageSize * 0.4));
    const auxPlaceholderIconSize = Math.max(
      8,
      Math.round(Math.min(resolvedAuxSlotSize, 28) * 0.48)
    );
    const auxPlaceholderLabelSize = Math.max(
      6,
      Math.round(Math.min(resolvedAuxSlotSize, 24) * 0.5)
    );
    const isCollapsed = collapsedSections.has(sectionKey);
    const isDropTarget =
      (mode === 'crops-selected' ||
        (mode === 'merge-source' && designTarget.designId !== mergeSourceDesignId)) &&
      designTarget.type !== 'pending';
    const isClickable = isDropTarget || isMergeSource || mode === 'idle';
    const handleDesignTextClick = (
      event: React.MouseEvent<HTMLElement>,
      copyValue: string | null | undefined,
      successMessage: string
    ) => {
      event.stopPropagation();
      if (selectedCropIds.size > 0) {
        onSectionClick(designTarget);
        return;
      }
      if (!copyValue) {
        return;
      }
      void copyText(copyValue, successMessage);
    };

    const refBoxStyle: React.CSSProperties = {
      width: refImgSize,
      height: refImgSize,
      flexShrink: 0,
      position: 'relative',
      overflow: 'visible',
      borderRadius: 3,
    };
    const renderAuxMediaSlot = (
      key: string,
      options: {
        imageUrl?: string;
        alt: string;
        title: string;
        label?: string;
        icon?: React.ReactNode;
        meta?: ZoomMeta;
      }
    ) => {
      const imageUrl = options.imageUrl;

      return (
        <div
          key={key}
          data-testid={key}
          aria-label={options.title}
          style={{
            width: resolvedAuxSlotSize,
            minWidth: resolvedAuxSlotSize,
            flex: '1 1 0',
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${barcodeTheme.cardBorder}`,
            background: imageUrl ? '#000' : 'var(--elevated)',
            color: 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          {imageUrl ? (
            <CachedNonCropImage
              imageUrl={imageUrl}
              alt={options.alt}
              debugKey={`${key}:${imageUrl}`}
              meta={options.meta}
              testId={`${key}-image`}
              containerStyle={{ width: '100%', height: '100%' }}
              imageStyle={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
                background: '#000',
              }}
              fallbackStyle={{ background: '#000' }}
              onImageHover={onImageHover}
              onImageMove={onImageMove}
              onImageLeave={onImageLeave}
            />
          ) : options.icon ? (
            options.icon
          ) : (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: scaleText(auxPlaceholderLabelSize),
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1,
              }}
            >
              {options.label}
            </span>
          )}
        </div>
      );
    };

    const renderGroupStrip = (
      label: string,
      groupDesigns: ForgeDesignGroupSummary[],
      tone: string
    ) => {
      if (groupDesigns.length === 0) return null;
      const previewSize = Math.max(34, Math.min(52, Math.round(resolvedCropSize * 0.34)));
      return (
        <div
          data-testid={`design-group-strip-${label}-${design.id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 5,
            minWidth: 0,
          }}
        >
          <span
            style={{
              flex: '0 0 auto',
              minWidth: 44,
              fontFamily: 'var(--font-mono)',
              fontSize: scaleText(8),
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: tone,
            }}
          >
            {label}
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              padding: '1px 0 2px',
              scrollbarWidth: 'thin',
            }}
          >
            {groupDesigns.map((member) => {
              const isMemberHighlighted = highlightedDesignId === member.designId;
              const memberZoomMeta: ZoomMeta = {
                metadataType: 'design',
                id: member.designId,
                normalizedBarcode: member.normalizedBarcode ?? undefined,
                barcodeStatus: member.barcodeStatus ?? undefined,
                createdAt: member.createdAt ?? undefined,
                updatedAt: member.updatedAt ?? undefined,
                updatedBy: member.updatedBy ?? undefined,
                cropCount: member.cropCount ?? undefined,
                noteCount: member.noteCount ?? 0,
              };

              return (
                <button
                  key={`${label}-${member.designId}`}
                  type="button"
                  data-testid={`design-group-card-${label}-${member.designId}`}
                  aria-label={`Load ${label.toLowerCase()} group design ${
                    member.normalizedBarcode || member.designId
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onLoadDesignGroupMember?.(member);
                  }}
                  onMouseEnter={(event) => onMetadataHover?.(event, memberZoomMeta)}
                  onMouseMove={(event) => onMetadataMove?.(event, memberZoomMeta)}
                  onMouseLeave={onMetadataLeave}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: previewSize + 10,
                    height: previewSize + 10,
                    padding: 4,
                    borderRadius: 12,
                    border: isMemberHighlighted
                      ? `2px solid ${highlightColor || tone}`
                      : `1px solid color-mix(in srgb, ${barcodeTheme.cardBorder} 70%, ${tone} 30%)`,
                    borderLeft: `4px solid ${tone}`,
                    background: `linear-gradient(135deg, color-mix(in srgb, ${barcodeTheme.nestedSurface} 90%, ${tone} 10%), color-mix(in srgb, ${barcodeTheme.cardBackground} 88%, ${tone} 12%))`,
                    color: 'var(--text-secondary)',
                    cursor: onLoadDesignGroupMember ? 'pointer' : 'default',
                    flex: '0 0 auto',
                    boxShadow: isMemberHighlighted
                      ? highlightGlow ||
                        '0 0 14px rgba(188,140,255,0.74), 0 0 30px rgba(188,140,255,0.34)'
                      : `inset 0 0 0 1px color-mix(in srgb, ${barcodeTheme.cardBorder} 48%, transparent), 0 4px 14px rgba(0,0,0,0.12)`,
                  }}
                >
                  <ProductImagePreview
                    imageUrl={member.previewVizUrl}
                    alt={member.normalizedBarcode || member.designId}
                    size={previewSize}
                    borderColor={`color-mix(in srgb, ${tone} 68%, ${barcodeTheme.cardBorder} 32%)`}
                    borderRadius={9}
                    meta={memberZoomMeta}
                    onImageHover={onImageHover}
                    onImageMove={onImageMove}
                    onImageLeave={onImageLeave}
                  />
                </button>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div
        key={`${groupKeyPrefix}-${design.id}`}
        data-testid={`barcode-design-shell-${groupKeyPrefix}-${design.id}`}
        data-barcode-theme={barcodeTheme.key}
        style={{
          padding: `${chrome.designShellPadding}px`,
          marginBottom: chrome.designShellMarginBottom,
          borderRadius: 'var(--radius-sm)',
          background: isDesignHighlighted ? 'var(--purple-subtle)' : barcodeTheme.nestedSurface,
          boxShadow: isDesignHighlighted
            ? 'inset 0 0 0 1px var(--purple-border), var(--shadow-card)'
            : `inset 0 0 0 1px ${barcodeTheme.cardBorder}, var(--shadow-card)`,
          transition: 'all 150ms ease',
        }}
        onClick={() => {
          if (mode === 'idle')
            onActiveDesignChange(activeDesignId === design.id ? null : design.id);
        }}
      >
        <div
          onClick={(e) => {
            if (isDropTarget || isMergeSource || mode === 'idle') {
              e.stopPropagation();
              onSectionClick(designTarget);
            }
          }}
          onMouseEnter={(e) => {
            if (isDropTarget) {
              e.currentTarget.style.background =
                mode === 'merge-source' ? 'var(--positive-subtle)' : 'var(--purple-subtle)';
              e.currentTarget.style.borderLeftColor =
                mode === 'merge-source' ? 'var(--positive)' : 'var(--purple)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isMergeSource
              ? 'var(--danger-subtle)'
              : 'transparent';
            e.currentTarget.style.borderLeftColor = isMergeSource ? 'var(--danger)' : 'transparent';
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: chrome.designHeaderGap,
            padding: `${chrome.designHeaderPaddingY}px 10px ${chrome.designHeaderPaddingY}px 2px`,
            cursor: isClickable ? 'pointer' : 'default',
            borderRadius: 'var(--radius-sm)',
            borderLeft: isMergeSource ? '3px solid var(--danger)' : '3px solid transparent',
            border: `1px solid ${isMergeSource ? 'var(--danger)' : barcodeTheme.cardBorder}`,
            background: isMergeSource ? 'var(--danger-subtle)' : barcodeTheme.cardBackground,
            transition: 'all 150ms ease',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              width: 22,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {designCrops.length > 0 && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCollapsedSection(sectionKey);
                }}
                style={{ cursor: 'pointer', display: 'flex' }}
              >
                {isCollapsed ? (
                  <KeyboardArrowRightIcon
                    style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    style={{ fontSize: scaleIcon(16), color: 'var(--text-tertiary)' }}
                  />
                )}
              </div>
            )}
          </div>
          {isConfirmed && design.frontImageUrl ? (
            <div data-testid={`design-front-image-${design.id}`} style={{ ...refBoxStyle }}>
              <CachedNonCropImage
                imageUrl={design.frontImageUrl}
                alt="Front"
                debugKey={`design-front:${design.id}:${design.frontImageUrl}`}
                testId={`design-front-image-${design.id}-image`}
                containerStyle={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: 3,
                  border: `1px solid ${barcodeTheme.cardBorder}`,
                }}
                imageStyle={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  background: '#000',
                  display: 'block',
                }}
                fallbackStyle={{ background: '#000' }}
                onImageHover={onImageHover}
                onImageMove={onImageMove}
                onImageLeave={onImageLeave}
              />
              <CheckCircleIcon
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  fontSize: badgeSize,
                  color: '#E8976C',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
                }}
              />
            </div>
          ) : (
            <div style={{ ...refBoxStyle }}>
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: 3,
                  border: `1px dashed ${isBarcPending ? 'var(--warning)' : 'var(--border-hover)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: barcodeTheme.cardBackground,
                }}
              >
                {isBarcPending ? (
                  <HourglassEmptyIcon
                    style={{ fontSize: iconSize, color: 'var(--warning)', opacity: 0.5 }}
                  />
                ) : (
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: scaleText(10),
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      color: 'var(--text-tertiary)',
                      opacity: 0.45,
                      lineHeight: 1,
                    }}
                  >
                    F
                  </span>
                )}
              </div>
              <CheckCircleIcon
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  fontSize: badgeSize,
                  color: 'var(--text-tertiary)',
                  opacity: 0.4,
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
                }}
              />
            </div>
          )}
          {repCrop ? (
            (() => {
              const repMeta = buildCropZoomMeta(resolvePreviewCrop(repCrop));
              const isRepSelected = selectedCropIds.has(repCrop.id);
              const isRepHighlighted = highlightedCropIds?.has(repCrop.id) ?? false;
              const isRepFocused = Boolean(
                focusedHighlight &&
                (focusedHighlight.cropId === repCrop.id ||
                  focusedHighlight.cropIds?.includes(repCrop.id))
              );
              return (
                <div
                  data-testid={`design-representative-image-${design.id}`}
                  style={{ ...refBoxStyle }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      borderRadius: 3,
                      cursor: 'default',
                      border: isRepSelected
                        ? '2px solid var(--blue)'
                        : isRepHighlighted || isRepFocused
                          ? `2.16px solid ${highlightColor || 'var(--purple)'}`
                          : `1px solid ${barcodeTheme.cardBorder}`,
                      boxShadow:
                        isRepHighlighted || isRepFocused
                          ? highlightGlow ||
                            '0 0 14px rgba(188,140,255,0.74), 0 0 30px rgba(188,140,255,0.34)'
                          : 'none',
                    }}
                  >
                    <CachedNonCropImage
                      imageUrl={repCrop.imageUrl}
                      alt="Rep"
                      debugKey={`design-representative:${design.id}:${repCrop.imageUrl}`}
                      testId={`design-representative-image-${design.id}-image`}
                      meta={repMeta}
                      containerStyle={{ width: '100%', height: '100%' }}
                      imageStyle={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        background: '#000',
                        display: 'block',
                      }}
                      fallbackStyle={{ background: '#000' }}
                      onImageHover={onImageHover}
                      onImageMove={onImageMove}
                      onImageLeave={onImageLeave}
                    />
                  </div>
                  <StarIcon
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      fontSize: badgeSize,
                      color: '#E8976C',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
                    }}
                  />
                </div>
              );
            })()
          ) : (
            <div
              style={{
                ...refBoxStyle,
                overflow: 'hidden',
                border: '1px dashed var(--border-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: barcodeTheme.cardBackground,
              }}
            >
              <StarIcon
                style={{ fontSize: iconSize, color: 'var(--text-tertiary)', opacity: 0.2 }}
              />
            </div>
          )}
          <div
            data-testid={`design-aux-stack-${design.id}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: resolvedAuxGap,
              width: resolvedAuxSlotSize,
              minWidth: resolvedAuxSlotSize,
              height: resolvedAuxStackHeight,
              flexShrink: 0,
            }}
          >
            {renderAuxMediaSlot(`back-${design.id}`, {
              imageUrl: isConfirmed ? design.backImageUrl : undefined,
              alt: 'Back',
              label: 'B',
              title: isConfirmed && design.backImageUrl ? 'Back image' : 'No back image',
              meta: isConfirmed && design.backImageUrl ? { source: 'design back' } : undefined,
            })}
            {renderAuxMediaSlot(`barcode-${design.id}`, {
              imageUrl: isConfirmed ? design.barcodeImageUrl : undefined,
              alt: 'Barcode',
              icon: (
                <QrCode2OutlinedIcon
                  style={{ fontSize: auxPlaceholderIconSize, color: 'var(--text-tertiary)' }}
                />
              ),
              title: isConfirmed && design.barcodeImageUrl ? 'Barcode image' : 'No barcode image',
              meta:
                isConfirmed && design.barcodeImageUrl ? { source: 'design barcode' } : undefined,
            })}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <OverflowTitleText
              testId={`design-title-${design.id}`}
              titleText={designTitleText}
              ariaLabel={`Copy design name ${designTitleText}`}
              onClick={
                designTitleText !== 'Unknown'
                  ? (event) => {
                      handleDesignTextClick(event, designTitleText, 'Copied design name');
                    }
                  : undefined
              }
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: scaleText(11),
                fontWeight: 600,
                color: isMergeSource ? 'var(--danger)' : barcodeTheme.upcText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: designTitleText !== 'Unknown' ? 'pointer' : 'default',
              }}
            >
              {designTitleText}
            </OverflowTitleText>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                minWidth: 0,
                fontFamily: 'var(--font-mono)',
                fontSize: scaleText(9),
                color: barcodeTheme.supportingText,
              }}
            >
              {designBarcodeText ? (
                <OverflowTitleText
                  as="span"
                  testId={`design-barcode-${design.id}`}
                  titleText={designBarcodeText}
                  ariaLabel={`Copy design barcode ${designBarcodeText}`}
                  onClick={(event) => {
                    handleDesignTextClick(event, designBarcodeText, 'Copied design barcode');
                  }}
                  style={{
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                >
                  {designBarcodeText}
                </OverflowTitleText>
              ) : null}
              <span style={{ minWidth: 0, display: 'inline-flex' }}>
                <OverflowTitleText
                  as="span"
                  testId={`design-id-${design.id}`}
                  titleText={design.id}
                  ariaLabel={`Copy design ID ${design.id}`}
                  onClick={(event) => {
                    handleDesignTextClick(event, design.id, 'Copied design ID');
                  }}
                  onMouseEnter={(event) => onMetadataHover?.(event, designZoomMeta)}
                  onMouseMove={(event) => onMetadataMove?.(event, designZoomMeta)}
                  onMouseLeave={onMetadataLeave}
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  {design.id}
                </OverflowTitleText>
              </span>
            </div>
          </div>
          {onOpenDesignGroups ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpenDesignGroups(design, upc);
              }}
              style={{
                border: `1px solid color-mix(in srgb, ${barcodeTheme.cardBorder} 88%, transparent)`,
                background: 'color-mix(in srgb, var(--surface) 78%, transparent)',
                color: 'color-mix(in srgb, var(--text-secondary) 72%, transparent)',
                borderRadius: 8,
                width: 28,
                height: 28,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: 'pointer',
                flexShrink: 0,
                opacity: 0.82,
              }}
              aria-label="Manage design groups"
              data-testid={`design-groups-button-${design.id}`}
            >
              <LinkOutlinedIcon style={{ fontSize: 16, opacity: 0.88 }} />
            </button>
          ) : null}
          {onEditDesignNote ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onEditDesignNote(design, upc);
              }}
              style={{
                border: `1px solid ${
                  noteCount > 0
                    ? 'color-mix(in srgb, var(--warning) 70%, transparent)'
                    : `color-mix(in srgb, ${barcodeTheme.cardBorder} 88%, transparent)`
                }`,
                background:
                  noteCount > 0
                    ? 'color-mix(in srgb, var(--warning) 18%, var(--surface))'
                    : 'color-mix(in srgb, var(--surface) 78%, transparent)',
                color:
                  noteCount > 0
                    ? 'var(--warning)'
                    : 'color-mix(in srgb, var(--text-secondary) 72%, transparent)',
                borderRadius: 8,
                width: 28,
                height: 28,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                opacity: noteCount > 0 ? 1 : 0.82,
              }}
              aria-label={
                noteCount > 0
                  ? `Edit ${noteCount} design note${noteCount === 1 ? '' : 's'}`
                  : 'Add design note'
              }
              data-testid={`design-note-button-${design.id}`}
            >
              <NotesOutlinedIcon style={{ fontSize: 16, opacity: 0.9 }} />
              {noteCount > 0 ? (
                <span
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    minWidth: 13,
                    height: 13,
                    borderRadius: 999,
                    background: 'var(--warning)',
                    color: '#111827',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 8,
                    fontWeight: 800,
                    lineHeight: '13px',
                  }}
                >
                  {noteCount}
                </span>
              ) : null}
            </button>
          ) : null}
        </div>
        {renderGroupStrip('Full', design.fullGroupDesigns ?? [], 'var(--positive)')}
        {renderGroupStrip('Partial', design.partialGroupDesigns ?? [], 'var(--warning)')}
        {!isCollapsed && showCropStrip && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: chrome.designCropStripGap,
              padding: `${chrome.designCropStripPaddingTop}px 8px ${chrome.designCropStripPaddingBottom}px 30px`,
              marginTop: 2,
              borderLeft: `2px solid ${barcodeTheme.nestedRail}`,
              marginLeft: 4,
            }}
          >
            {renderCropGrid(
              designCrops,
              `design-crop-grid-${design.id}`,
              (crop) => getCropSectionType(resolvePreviewCrop(crop)),
              {
                groupKey: `var-${design.id}`,
                representativeCropId: design.representativeCropId,
                designId: design.id,
                frontFirst: true,
              }
            )}
          </div>
        )}
      </div>
    );
  };

  const renderInlineItemNumber = () => {
    if (!upc.itemNumber) return null;
    const hasItemNumber = upc.actualItemNumber !== null;
    const displayValue = hasItemNumber ? upc.actualItemNumber || upc.itemNumber : 'no item_number';
    const copyValue = hasItemNumber ? upc.actualItemNumber || upc.itemNumber : upc.columnKey;
    const totalCropCount = countMasterLabelCrops(upc);
    const cropCountLabel = `${totalCropCount.toLocaleString()} crop${totalCropCount === 1 ? '' : 's'}`;

    const baseMetaTextStyle: React.CSSProperties = {
      fontFamily: 'var(--font-mono)',
      fontSize: scaleText(9),
      lineHeight: 1,
      color: 'var(--text-tertiary)',
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      minWidth: 0,
      maxWidth: '100%',
    };
    const itemNumberValueStyle: React.CSSProperties = {
      color: 'var(--text-secondary)',
      flexShrink: 0,
    };
    const cropCountStyle: React.CSSProperties = {
      color: 'var(--text-tertiary)',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      maxWidth: 92,
      minWidth: 0,
      flexShrink: 9999,
    };

    return (
      <span
        role="button"
        tabIndex={0}
        data-testid={`upc-header-item-meta-${columnId}`}
        aria-label={
          hasItemNumber ? `Copy item number ${copyValue}` : `Copy column key ${copyValue}`
        }
        onClick={() => {
          void copyText(copyValue, hasItemNumber ? 'Copied item number' : 'Copied column key');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            void copyText(copyValue, hasItemNumber ? 'Copied item number' : 'Copied column key');
          }
        }}
        style={{
          ...baseMetaTextStyle,
          gap: 5,
          cursor: 'pointer',
          outline: 'none',
          userSelect: 'none',
        }}
      >
        <span style={{ color: 'var(--text-quaternary)' }}>·</span>
        <span style={itemNumberValueStyle}>{displayValue}</span>
        <span style={{ color: 'var(--text-quaternary)', flexShrink: 0 }}>·</span>
        <span data-testid={`upc-header-crop-count-${columnId}`} style={cropCountStyle}>
          {cropCountLabel}
        </span>
      </span>
    );
  };

  // ── Main render ──

  return (
    <div
      style={{
        flex: 1,
        minWidth: 200,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
      {/* UPC header */}
      <div
        style={{
          borderBottom: '1px solid var(--border)',
          padding: `${chrome.columnHeaderPaddingY}px 8px`,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: chrome.columnHeaderGap,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0,
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <div
                data-testid={`upc-header-primary-row-${columnId}`}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  gap: 8,
                  rowGap: chrome.headerPrimaryRowGap,
                  minWidth: 0,
                }}
              >
                <OverflowTitleText
                  as="div"
                  testId={`upc-header-product-name-${columnId}`}
                  titleText={upc.productName}
                  style={{
                    minWidth: 0,
                    maxWidth: '100%',
                    flexShrink: 1,
                    fontFamily: 'var(--font-mono)',
                    fontSize: scaleText(11),
                    lineHeight: 1,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {upc.productName}
                </OverflowTitleText>
                {renderInlineItemNumber()}
              </div>
            </div>
            <div
              data-testid={`upc-header-actions-${columnId}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 4,
                flexShrink: 0,
                alignSelf: 'flex-start',
              }}
            >
              {controlEmbeddingsUrl ? (
                <a
                  href={controlEmbeddingsUrl}
                  data-testid={`upc-control-link-${columnId}`}
                  aria-label="Open this item in Control embeddings detail"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: chrome.headerActionInset,
                    color: 'var(--text-quaternary)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    textDecoration: 'none',
                    fontFamily: 'var(--font-mono)',
                    fontSize: scaleText(10),
                    fontWeight: 700,
                    opacity: 0.82,
                  }}
                >
                  [c]
                </a>
              ) : null}
              {onOpenDataCollection ? (
                <button
                  type="button"
                  onClick={() => onOpenDataCollection(upc)}
                  data-testid={`upc-data-collection-button-${columnId}`}
                  aria-label="Open data collection request"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: chrome.headerActionInset,
                    color: 'var(--text-quaternary)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    fontFamily: 'var(--font-mono)',
                    fontSize: scaleText(10),
                    fontWeight: 700,
                    opacity: 0.82,
                  }}
                >
                  [dc]
                </button>
              ) : null}
              {isFocusedColumn && onClearFocusedColumn ? (
                <button
                  onClick={onClearFocusedColumn}
                  data-testid={`upc-unfocus-button-${columnId}`}
                  aria-label="Show all columns"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: chrome.headerActionInset,
                    color: 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ZoomOutOutlinedIcon style={{ fontSize: scaleIcon(14) }} />
                </button>
              ) : canFocusColumn && onFocusColumn ? (
                <button
                  onClick={() => onFocusColumn(columnId)}
                  data-testid={`upc-focus-button-${columnId}`}
                  aria-label="Focus this column"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: chrome.headerActionInset,
                    color: 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ZoomInOutlinedIcon style={{ fontSize: scaleIcon(14) }} />
                </button>
              ) : null}
              {onRemoveUpc && (
                <button
                  onClick={() => onRemoveUpc(columnId)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: chrome.headerActionInset,
                    color: 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CloseIcon style={{ fontSize: scaleIcon(14) }} />
                </button>
              )}
            </div>
          </div>
          {(upc.warnings ?? []).length > 0 && (
            <div
              style={{
                marginTop: chrome.warningMarginTop,
                padding: `${chrome.warningPaddingY}px 8px`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--warning-subtle)',
                border: '1px solid rgba(227, 179, 65, 0.28)',
                display: 'flex',
                flexDirection: 'column',
                gap: chrome.warningGap,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: 'var(--font-mono)',
                  fontSize: scaleText(9),
                  color: 'var(--warning)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                <WarningAmberOutlinedIcon style={{ fontSize: 12 }} />
                Design warnings
              </div>
              {(upc.warnings ?? []).map((warning, index) => (
                <div
                  key={`${warning.code}-${warning.designId || 'item'}-${index}`}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: scaleText(9),
                    lineHeight: 1.45,
                    color: 'var(--text-secondary)',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {warning.message}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable sections — fixed order: Pending, Unassigned, Designs, Carton, Non-front */}
      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: `${chrome.sectionScrollPaddingY}px 0`,
          display: 'flex',
          flexDirection: 'column',
          gap: chrome.sectionStackGap,
        }}
      >
        {/* ── Group: Barcodes & Designs (blue-grey) ── */}
        {barcodeRows.length > 0 && (
          <div
            style={{
              borderLeft: '2px solid var(--accent-border)',
              marginLeft: 1,
              background: 'var(--accent-subtle)',
              borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
              paddingTop: chrome.sectionScrollPaddingY,
              paddingBottom: chrome.sectionScrollPaddingY,
            }}
          >
            <div style={{ padding: '0 4px' }}>
              {renderStaticHeader('BARCODES & DESIGNS', upcCount, `barcodes-${columnId}`)}
              {!collapsedSections.has(`barcodes-${columnId}`) && (
                <div
                  ref={barcodeSectionRef}
                  data-testid={`barcode-rows-${columnId}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: `${chrome.barcodeSectionRowsPaddingTop}px 8px ${chrome.barcodeSectionRowsPaddingBottom}px 0`,
                  }}
                >
                  {barcodeRows.map((row) => {
                    const rowTheme =
                      barcodeGroupThemeByNormalized.get(row.normalizedBarcode)?.theme ??
                      getMasterBarcodeGroupTheme(0);
                    const rowSectionKey = `barcode-row-${columnId}-${row.normalizedBarcode}`;
                    const isRowCollapsed = collapsedSections.has(rowSectionKey);
                    return renderResponsiveBarcodeRowCard(
                      row,
                      rowTheme,
                      rowSectionKey,
                      isRowCollapsed
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Group: Cartons (orange) ── */}
        <div
          style={{
            borderLeft: '2px solid rgba(232,151,108,0.35)',
            marginLeft: 1,
            background: 'rgba(232,151,108,0.04)',
            borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
            paddingTop: chrome.sectionBandPaddingY,
            paddingBottom: chrome.sectionBandPaddingY,
          }}
        >
          {(() => {
            const cartonCrops = sorted(upc.cartonCrops);
            const cartonSectionKey = `carton-${columnId}`;
            const cartonTarget: SectionTarget = { itemNumber: columnId, type: 'carton' };
            const isCartonHighlighted = checkHighlight(cartonSectionKey, cartonTarget);
            return (
              <div
                style={{
                  padding: '0 4px',
                  marginBottom: chrome.sectionBandMarginBottom,
                  borderRadius: 'var(--radius-sm)',
                  background: isCartonHighlighted ? 'var(--purple-subtle)' : 'transparent',
                  boxShadow: isCartonHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
                  transition: 'all 150ms ease',
                }}
              >
                {renderSectionHeader('Cartons', cartonCrops.length, cartonSectionKey, cartonTarget)}
                {!collapsedSections.has(cartonSectionKey) &&
                  renderCropGrid(
                    cartonCrops,
                    `section-crop-grid-carton-${columnId}`,
                    () => 'carton',
                    {
                      groupKey: `carton-${columnId}`,
                    }
                  )}
              </div>
            );
          })()}
        </div>

        {/* ── Group: Pending (purple) ── */}
        <div
          style={{
            borderLeft: '2px solid rgba(163,113,247,0.35)',
            marginLeft: 1,
            background: 'rgba(163,113,247,0.04)',
            borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
            paddingTop: chrome.sectionBandPaddingY,
            paddingBottom: chrome.sectionBandPaddingY,
          }}
        >
          {(() => {
            const pending = sorted(upc.pendingCrops || []);
            const pendingSectionKey = `pending-${columnId}`;
            const pendingTarget: SectionTarget = { itemNumber: columnId, type: 'pending' };
            const isPendingHighlighted = checkHighlight(
              pendingSectionKey,
              pendingTarget,
              pending.length
            );
            return (
              <div
                style={{
                  padding: '0 4px',
                  marginBottom: chrome.sectionBandMarginBottom,
                  borderRadius: 'var(--radius-sm)',
                  background: isPendingHighlighted ? 'var(--purple-subtle)' : 'transparent',
                  boxShadow: isPendingHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
                  transition: 'all 150ms ease',
                }}
              >
                {renderSectionHeader('Pending', pending.length, pendingSectionKey, pendingTarget)}
                {pending.length > 0 &&
                  !collapsedSections.has(pendingSectionKey) &&
                  renderCropGrid(
                    pending,
                    `section-crop-grid-pending-${columnId}`,
                    (crop) => getCropSectionType(resolvePreviewCrop(crop)),
                    {
                      groupKey: `pending-${columnId}`,
                      pending: true,
                    }
                  )}
              </div>
            );
          })()}
        </div>

        {/* ── Group: Unassigned (grey) ── */}
        <div
          style={{
            borderLeft: '2px solid rgba(139,148,158,0.35)',
            marginLeft: 1,
            background: 'rgba(139,148,158,0.04)',
            borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
            paddingTop: chrome.sectionBandPaddingY,
            paddingBottom: chrome.sectionBandPaddingY,
          }}
        >
          {(() => {
            const unassignedCrops = sorted(upc.unassignedCrops);
            const unassignedSectionKey = `novar-${columnId}`;
            const unassignedTarget: SectionTarget = { itemNumber: columnId, type: 'no-design' };
            const isUnassignedHighlighted = checkHighlight(
              unassignedSectionKey,
              unassignedTarget,
              unassignedCrops.length
            );
            return (
              <div
                style={{
                  padding: '0 4px',
                  marginBottom: chrome.sectionBandMarginBottom,
                  borderRadius: 'var(--radius-sm)',
                  background: isUnassignedHighlighted ? 'var(--purple-subtle)' : 'transparent',
                  boxShadow: isUnassignedHighlighted
                    ? 'inset 0 0 0 1px var(--purple-border)'
                    : 'none',
                  transition: 'all 150ms ease',
                }}
              >
                {renderSectionHeader(
                  'Unassigned Products',
                  unassignedCrops.length,
                  unassignedSectionKey,
                  unassignedTarget
                )}
                {unassignedCrops.length > 0 &&
                  !collapsedSections.has(unassignedSectionKey) &&
                  renderCropGrid(
                    unassignedCrops,
                    `section-crop-grid-unassigned-${columnId}`,
                    (crop) => getCropSectionType(resolvePreviewCrop(crop)),
                    {
                      groupKey: `unassigned-${columnId}`,
                    }
                  )}
              </div>
            );
          })()}
        </div>

        {/* ── Group: Inactive (red) ── */}
        <div
          style={{
            borderLeft: '2px solid rgba(248,81,73,0.35)',
            marginLeft: 1,
            background: 'rgba(248,81,73,0.04)',
            borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
            paddingTop: chrome.sectionBandPaddingY,
            paddingBottom: chrome.sectionBandPaddingY,
          }}
        >
          {(() => {
            const inactiveCrops = sorted(upc.inactiveCrops || []);
            const inactiveSectionKey = `inactive-${columnId}`;
            return (
              <div style={{ padding: '0 4px', marginBottom: chrome.sectionBandMarginBottom }}>
                {renderSectionHeader('Inactive', inactiveCrops.length, inactiveSectionKey, {
                  itemNumber: columnId,
                  type: 'inactive',
                })}
                {inactiveCrops.length > 0 &&
                  !collapsedSections.has(inactiveSectionKey) &&
                  renderCropGrid(
                    inactiveCrops,
                    `section-crop-grid-inactive-${columnId}`,
                    (crop) => getCropSectionType(resolvePreviewCrop(crop)),
                    {
                      groupKey: `inactive-${columnId}`,
                    }
                  )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
